import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag,
  Mail,
  Phone,
  Building,
  MapPin,
  Calendar,
  Clock,
  Users,
  CheckCircle2,
  X,
  ArrowLeft,
  Loader2,
  Check,
} from "lucide-react";
import Header from "../components/user/Header";
import Footer from "../components/user/Footer";
import FloatingActions from "../components/user/FloatingActions";
import { useNotification } from "../context/NotificationContext";
import { formatPrice } from "../utils/productUtils";
import { useCheckout } from "../services/user/CheckoutService";

function SkeletonCheckout() {
  return (
    <div className="min-h-screen bg-white dark:bg-[#121212]">
      <Header />
      <div className="max-w-[1200px] mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column Skeleton */}
          <div className="lg:col-span-2 space-y-6">
            {Array.from({ length: 3 }).map((_, idx) => (
              <div
                key={idx}
                className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-6 border border-[#E9E9E9] dark:border-[#333333]"
              >
                <div className="h-6 w-48 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded mb-6 animate-shimmer"></div>
                <div className="space-y-4">
                  <div className="h-12 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded-lg animate-shimmer"></div>
                  <div className="h-12 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded-lg animate-shimmer"></div>
                </div>
              </div>
            ))}
          </div>
          {/* Right Column Skeleton */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-6 border border-[#E9E9E9] dark:border-[#333333] sticky top-24">
              <div className="h-6 w-32 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded mb-6 animate-shimmer"></div>
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div
                    key={idx}
                    className="h-16 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded-lg animate-shimmer"
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style jsx="true">{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        .animate-shimmer {
          background-size: 200% 100%;
          animation: shimmer 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

function SuccessModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop - no click handler to prevent accidental dismissal */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <div className="relative bg-white dark:bg-[#1E1E1E] rounded-3xl p-8 max-w-md w-full shadow-2xl transform animate-scaleIn">
        <button
          onClick={() => {
            localStorage.removeItem("cart");
            localStorage.removeItem("checkoutForm");
            window.location.href = "/rentals";
          }}
          className="absolute top-4 right-4 text-[#999999] hover:text-black dark:hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-[#10B981] to-[#059669] rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 size={40} className="text-white" />
          </div>

          <h2
            className="text-2xl font-medium font-serif text-black dark:text-white mb-3"
          >
            Order Placed Successfully! 🎉
          </h2>

          <p className="text-[#666666] dark:text-[#A0A0A0] mb-6 leading-relaxed">
            Thank you for your order! An invoice has been sent to your email
            address. A member of our team will reach out to you shortly.
          </p>

          <div className="w-full bg-[#F5F3FF] dark:bg-[#2A2440] rounded-xl p-4 mb-6">
            <p className="text-sm text-[#62397f] dark:text-[#A78BFA] font-medium">
              📧 Expected Response Time:
            </p>
            <p className="text-sm text-[#666666] dark:text-[#A0A0A0] mt-1">
              {(() => {
                const hour = new Date().getHours();
                if (hour >= 8 && hour < 21) {
                  return "Within 1-2 hours";
                } else {
                  return "First thing the following day";
                }
              })()}
            </p>
          </div>

          <div class="flex flex-col sm:flex-row w-full gap-4">
            <button
              onClick={() => {
                localStorage.removeItem("cart");
                localStorage.removeItem("checkoutForm");
                window.location.href = "/";
              }}
              className="flex-1 px-6 py-4 bg-white dark:bg-[#2A2440] border-2 border-[#62397f] dark:border-[#A78BFA] hover:bg-[#F5F3FF] dark:hover:bg-[#3B3259] text-[#62397f] dark:text-[#A78BFA] font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02]"
            >
              Return Home
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("cart");
                localStorage.removeItem("checkoutForm");
                window.location.href = "/rentals";
              }}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-[#62397f] to-[#7C3AED] hover:from-[#7C3AED] hover:to-[#6D28D9] text-white font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02]"
            >
              Continue Rentals
            </button>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default function CheckoutPage() {
  const [isLoading, setIsLoading] = useState(true);
  const { showNotification } = useNotification();
  const [cartItems, setCartItems] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Checkout Service
  const {
    loading: serviceLoading,
    checkEmail,
    sendOtp: sendOtpAPI,
    verifyOtp: verifyOtpAPI,
    submitCheckout,
    getInvoiceSettings
  } = useCheckout();

  // Settings for fees
  const [settings, setSettings] = useState({
    service_charge_percent: 10.0,
    vat_percent: 7.5
  });

  // Email state tracking
  const [emailChecked, setEmailChecked] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Ref to track debounce timer
  const emailDebounceTimer = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    companyName: "",
    companyAddress: "",
    email: "",
    venueAddress: "",
    eventDate: "",
    deliveryDate: "",
    deliveryTime: "",
    eventPosition: "Downstairs",
    eventType: "",
    contactPersonName: "",
    contactPersonPhone: "",
    otp: "",
  });

  const [errors, setErrors] = useState({});


  // Load cart and form data on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    const loadData = async () => {
      setIsLoading(true);

      const savedCart = localStorage.getItem("cart");
      if (savedCart) {
        setCartItems(JSON.parse(savedCart));
      }

      // Fetch invoice settings
      const settingsData = await getInvoiceSettings();
      if (settingsData) {
        setSettings(settingsData);
      }

      const savedForm = localStorage.getItem("checkoutForm");
      if (savedForm) {
        const parsed = JSON.parse(savedForm);
        setFormData(parsed);
      }

      setIsLoading(false);
    };

    loadData();
  }, [getInvoiceSettings]);

  // Persist form data to localStorage
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem("checkoutForm", JSON.stringify(formData));
    }
  }, [formData, isLoading]);

  // Email check with debouncer
  const handleEmailCheck = useCallback(async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailExists(false);
      setEmailChecked(false);
      return;
    }

    const result = await checkEmail(email);
    setEmailChecked(true);
    if (result.exists) {
      setEmailExists(true);
      setOtpVerified(true);
      // Disable and clear company fields as requested
      setFormData(prev => ({
        ...prev,
        companyName: result.customer.full_name_or_company_name,
        companyAddress: result.customer.address,
        contactPersonName: result.customer.contact_person_name,
        contactPersonPhone: result.customer.contact_person_phone
      }));
    } else if (result.email_verified) {
      setEmailExists(false);
      setOtpVerified(true);
    } else {
      setEmailExists(false);
      setOtpVerified(false);
    }
  }, [checkEmail]);

  // Debounced email check effect with 500ms delay
  useEffect(() => {
    if (emailDebounceTimer.current) {
      clearTimeout(emailDebounceTimer.current);
    }

    if (formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      emailDebounceTimer.current = setTimeout(() => {
        handleEmailCheck(formData.email);
      }, 800);
    } else {
      setEmailChecked(false);
      setEmailExists(false);
      setOtpVerified(false);
    }

    return () => {
      if (emailDebounceTimer.current) {
        clearTimeout(emailDebounceTimer.current);
      }
    };
  }, [formData.email, handleEmailCheck]);

  // OTP Resend Timer logic
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    // Reset all email-related states immediately when email changes
    if (name === "email") {
      setEmailChecked(false);
      setEmailExists(false);
      setOtpSent(false);
      setOtpVerified(false);
      setResendTimer(0);
    }
  };

  const handleSendOtp = async () => {
    if (!formData.email) return;
    const success = await sendOtpAPI(formData.email);
    if (success) {
      setOtpSent(true);
      setResendTimer(60); // 1 minute countdown
    }
  };

  const handleVerifyOtp = async () => {
    if (!formData.otp || formData.otp.length !== 6) {
      setErrors((prev) => ({
        ...prev,
        otp: "Please enter a valid 6-digit OTP",
      }));
      return;
    }

    const success = await verifyOtpAPI(formData.email, formData.otp);
    if (success) {
      setOtpVerified(true);
    } else {
      setErrors((prev) => ({ ...prev, otp: "Invalid OTP. Please try again." }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim())
      newErrors.companyName = "Company/Individual name is required";
    if (!formData.companyAddress.trim())
      newErrors.companyAddress = "Address is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (emailChecked && !otpVerified) {
      newErrors.email = "Please verify your email to proceed";
    }

    if (!formData.venueAddress.trim())
      newErrors.venueAddress = "Venue address is required";
    // Date Validations (cannot be in the past)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const checkDateNotPast = (dateStr) => {
      if (!dateStr) return false;
      const selected = new Date(dateStr);
      selected.setHours(0, 0, 0, 0);
      return selected >= today;
    };

    if (!formData.eventDate) {
      newErrors.eventDate = "Event date is required";
    } else if (!checkDateNotPast(formData.eventDate)) {
      newErrors.eventDate = "Event date cannot be in the past";
    }

    if (!formData.deliveryDate) {
      newErrors.deliveryDate = "Delivery date is required";
    } else if (!checkDateNotPast(formData.deliveryDate)) {
      newErrors.deliveryDate = "Delivery date cannot be in the past";
    }

    // Professional Delivery Time Validation (8 AM - 7 PM)
    if (!formData.deliveryTime) {
      newErrors.deliveryTime = "Delivery time is required";
    } else {
      const [hours] = formData.deliveryTime.split(':').map(Number);
      if (hours < 8 || hours >= 19) {
        newErrors.deliveryTime = "Delivery must be between 8:00 AM and 7:00 PM";
      }
    }

    if (!formData.eventType.trim())
      newErrors.eventType = "Event type is required";

    if (!formData.contactPersonName.trim())
      newErrors.contactPersonName = "Contact person name is required";

    // Phone validation: MUST start with '0' and be exactly 11 digits
    if (!formData.contactPersonPhone.trim()) {
      newErrors.contactPersonPhone = "Contact phone is required";
    } else if (!/^0\d{10}$/.test(formData.contactPersonPhone)) {
      newErrors.contactPersonPhone = "Phone number must start with '0' and be exactly 11 digits long.";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const currentErrors = validateForm();
    if (Object.keys(currentErrors).length > 0) {
      const firstError = Object.values(currentErrors)[0];
      
      // Auto-scroll to top so mobile users don't miss the fixed viewport notification
      window.scrollTo({ top: 0, behavior: "smooth" });
      
      showNotification({
        type: "failed",
        title: "Validation Error",
        message: String(firstError) || "Please check the highlighted fields.",
      });
      return;
    }

    const res = await submitCheckout({
      ...formData,
      cartItems,
      emailExists // Flag to tell backend if user exists
    });

    if (res.success) {
      setShowSuccessModal(true);
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0), 0);
  };

  const calculateServiceCharge = () => {
    const subtotal = calculateSubtotal();
    const scp = Number(settings?.service_charge_percent) || 0;
    return subtotal * (scp / 100);
  };

  const calculateVAT = () => {
    const subtotal = calculateSubtotal();
    const sc = calculateServiceCharge();
    const vp = Number(settings?.vat_percent) || 0;
    return (subtotal + sc) * (vp / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateServiceCharge() + calculateVAT();
  };

  if (isLoading) {
    return <SkeletonCheckout />;
  }

  return (
    <div
      className="min-h-screen bg-[#FAFAFA] dark:bg-[#121212]"
    >
      <Header />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#62397f] dark:text-[#A78BFA] hover:underline mb-4"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <h1
            className="text-3xl sm:text-4xl lg:text-3xl font-medium font-serif text-black dark:text-white mb-2"
          >
            Checkout
          </h1>
          <p className="text-[#666666] dark:text-[#A0A0A0]">
            Complete your rental order
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Left Column - Form Fields */}
            <div className="lg:col-span-2 space-y-6">
              {/* Company/Individual Information */}
              <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-6 border border-[#E9E9E9] dark:border-[#333333]">
                <h2 className="text-xl lg:text-lg font-bold text-black dark:text-white mb-6 flex items-center gap-2">
                  <Building size={24} className="text-[#62397f]" />
                  Company/Individual Information
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                      Company/Individual Name *
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      disabled={emailExists}
                      className={`w-full px-4 py-3 bg-[#F5F5F5] dark:bg-[#2A2A2A] border ${errors.companyName ? "border-red-500" : "border-[#E9E9E9] dark:border-[#333333]"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62397f] text-black dark:text-white disabled:opacity-75 disabled:cursor-not-allowed`}
                      placeholder="Enter company or your full name"
                    />
                    {errors.companyName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.companyName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                      Company/Individual Address *
                    </label>
                    <input
                      type="text"
                      name="companyAddress"
                      value={formData.companyAddress}
                      onChange={handleInputChange}
                      disabled={emailExists}
                      className={`w-full px-4 py-3 bg-[#F5F5F5] dark:bg-[#2A2A2A] border ${errors.companyAddress ? "border-red-500" : "border-[#E9E9E9] dark:border-[#333333]"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62397f] text-black dark:text-white disabled:opacity-75 disabled:cursor-not-allowed`}
                      placeholder="Enter your address"
                    />
                    {errors.companyAddress && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.companyAddress}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 pr-12 bg-[#F5F5F5] dark:bg-[#2A2A2A] border ${errors.email ? "border-red-500" : "border-[#E9E9E9] dark:border-[#333333]"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62397f] text-black dark:text-white`}
                        placeholder="your@email.com"
                      />
                      {serviceLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2
                            size={20}
                            className="text-[#62397f] animate-spin"
                          />
                        </div>
                      )}
                      {!serviceLoading && emailExists && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                          <span className="text-[10px] font-bold text-green-500 uppercase tracking-wider bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded">Existing User</span>
                          <Check size={20} className="text-green-500" />
                        </div>
                      )}
                      {!serviceLoading && otpVerified && !emailExists && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Check size={20} className="text-green-500" />
                        </div>
                      )}
                    </div>
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.email}
                      </p>
                    )}
                    {!serviceLoading &&
                      emailChecked &&
                      !emailExists &&
                      !otpVerified &&
                      formData.email &&
                      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
                        <div className="mt-2">
                          <p className="text-sm text-orange-500 dark:text-orange-400 mb-2">
                            New email detected. Please verify with OTP to proceed.
                          </p>
                          <button
                            type="button"
                            onClick={handleSendOtp}
                            disabled={serviceLoading || otpSent}
                            className="px-4 py-2 bg-[#62397f] hover:bg-[#7C3AED] disabled:bg-[#A78BFA] text-white text-sm font-medium rounded-lg transition-colors disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {serviceLoading ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />
                                Processing...
                              </>
                            ) : (
                              "Send OTP"
                            )}
                          </button>
                        </div>
                      )}
                  </div>

                  {/* OTP Input */}
                  {otpSent && !otpVerified && (
                    <div className="bg-[#F5F3FF] dark:bg-[#2A2440] rounded-lg p-4">
                      <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                        Enter OTP *
                      </label>
                      <p className="text-xs text-[#666666] dark:text-[#A0A0A0] mb-3">
                        A 6-digit code has been sent to {formData.email}
                      </p>
                      <div className="flex gap-2 mb-4">
                        <input
                          type="text"
                          name="otp"
                          value={formData.otp}
                          onChange={handleInputChange}
                          maxLength={6}
                          className={`flex-1 px-4 py-3 bg-white dark:bg-[#1E1E1E] border ${errors.otp ? "border-red-500" : "border-[#E9E9E9] dark:border-[#333333]"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62397f] text-black dark:text-white text-center tracking-[0.5em] font-bold text-xl`}
                          placeholder="000000"
                        />
                        <button
                          type="button"
                          onClick={handleVerifyOtp}
                          disabled={serviceLoading || formData.otp.length !== 6}
                          className="px-6 py-3 bg-[#62397f] hover:bg-[#7C3AED] disabled:bg-[#A78BFA] text-white font-bold rounded-lg transition-colors disabled:cursor-not-allowed"
                        >
                          Verify
                        </button>
                      </div>

                      {errors.otp && (
                        <p className="text-red-500 text-sm mb-3">
                          {errors.otp}
                        </p>
                      )}

                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#DDD6FE] dark:border-[#333333]">
                        <span className="text-xs text-[#666666] dark:text-[#A0A0A0]">
                          Didn't receive the code?
                        </span>
                        {resendTimer > 0 ? (
                          <span className="text-xs font-medium text-[#62397f]">
                            Resend OTP in {resendTimer}s
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSendOtp}
                            disabled={serviceLoading}
                            className="text-xs font-bold text-[#62397f] hover:text-[#7C3AED] hover:underline"
                          >
                            Resend OTP
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Event Details */}
              <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-6 border border-[#E9E9E9] dark:border-[#333333]">
                <h2 className="text-xl lg:text-lg font-bold text-black dark:text-white mb-6 flex items-center gap-2">
                  <Users size={24} className="text-[#62397f]" />
                  Event Details
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                      Venue Address *
                    </label>
                    <input
                      type="text"
                      name="venueAddress"
                      value={formData.venueAddress}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-[#F5F5F5] dark:bg-[#2A2A2A] border ${errors.venueAddress ? "border-red-500" : "border-[#E9E9E9] dark:border-[#333333]"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62397f] text-black dark:text-white`}
                      placeholder="Enter event venue address"
                    />
                    {errors.venueAddress && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.venueAddress}
                      </p>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                        Date of Event *
                      </label>
                      <input
                        type="date"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split("T")[0]}
                        className={`w-full px-4 py-3 bg-[#F5F5F5] dark:bg-[#2A2A2A] border ${errors.eventDate ? "border-red-500" : "border-[#E9E9E9] dark:border-[#333333]"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62397f] text-black dark:text-white`}
                      />
                      {errors.eventDate && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.eventDate}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                        Type of Event *
                      </label>
                      <select
                        name="eventType"
                        value={formData.eventType}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-[#F5F5F5] dark:bg-[#2A2A2A] border ${errors.eventType ? "border-red-500" : "border-[#E9E9E9] dark:border-[#333333]"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62397f] text-black dark:text-white`}
                      >
                        <option value="">Select event type</option>
                        <option value="Wedding">Wedding</option>
                        <option value="Birthday">Birthday</option>
                        <option value="Corporate">Corporate Event</option>
                        <option value="Conference">Conference</option>
                        <option value="Party">Party</option>
                        <option value="Other">Other</option>
                      </select>
                      {errors.eventType && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.eventType}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                      Event Position in Building *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="eventPosition"
                          value="Downstairs"
                          checked={formData.eventPosition === "Downstairs"}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-[#62397f] focus:ring-[#62397f]"
                        />
                        <span className="text-sm text-black dark:text-white">
                          Downstairs
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="eventPosition"
                          value="Upstairs"
                          checked={formData.eventPosition === "Upstairs"}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-[#62397f] focus:ring-[#62397f]"
                        />
                        <span className="text-sm text-black dark:text-white">
                          Upstairs
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Details */}
              <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-6 border border-[#E9E9E9] dark:border-[#333333]">
                <h2 className="text-xl lg:text-lg font-bold text-black dark:text-white mb-6 flex items-center gap-2">
                  <MapPin size={24} className="text-[#62397f]" />
                  Delivery Details
                </h2>

                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                        Date of Delivery *
                      </label>
                      <input
                        type="date"
                        name="deliveryDate"
                        value={formData.deliveryDate}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split("T")[0]}
                        className={`w-full px-4 py-3 bg-[#F5F5F5] dark:bg-[#2A2A2A] border ${errors.deliveryDate ? "border-red-500" : "border-[#E9E9E9] dark:border-[#333333]"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62397f] text-black dark:text-white`}
                      />
                      {errors.deliveryDate && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.deliveryDate}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                        Time of Delivery *
                      </label>
                      <input
                        type="time"
                        name="deliveryTime"
                        value={formData.deliveryTime}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-[#F5F5F5] dark:bg-[#2A2A2A] border ${errors.deliveryTime ? "border-red-500" : "border-[#E9E9E9] dark:border-[#333333]"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62397f] text-black dark:text-white`}
                      />
                      {errors.deliveryTime && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.deliveryTime}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-6 border border-[#E9E9E9] dark:border-[#333333]">
                <h2 className="text-xl lg:text-lg font-bold text-black dark:text-white mb-6 flex items-center gap-2">
                  <Phone size={24} className="text-[#62397f]" />
                  Contact Person
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                      Contact Person Name *
                    </label>
                    <input
                      type="text"
                      name="contactPersonName"
                      value={formData.contactPersonName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-[#F5F5F5] dark:bg-[#2A2A2A] border ${errors.contactPersonName ? "border-red-500" : "border-[#E9E9E9] dark:border-[#333333]"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62397f] text-black dark:text-white disabled:opacity-75 disabled:cursor-not-allowed`}
                      placeholder="Enter contact person full name"
                    />
                    {errors.contactPersonName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.contactPersonName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#333333] dark:text-[#E0E0E0] mb-2">
                      Contact Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="contactPersonPhone"
                      value={formData.contactPersonPhone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-[#F5F5F5] dark:bg-[#2A2A2A] border ${errors.contactPersonPhone ? "border-red-500" : "border-[#E9E9E9] dark:border-[#333333]"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62397f] text-black dark:text-white disabled:opacity-75 disabled:cursor-not-allowed`}
                      placeholder="+234 xxx xxx xxxx"
                    />
                    {errors.contactPersonPhone && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.contactPersonPhone}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-6 border border-[#E9E9E9] dark:border-[#333333] sticky top-24">
                <h2 className="text-xl lg:text-lg font-bold text-black dark:text-white mb-6 flex items-center gap-2">
                  <ShoppingBag size={24} className="text-[#62397f]" />
                  Rental Summary
                </h2>

                {cartItems.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-[#666666] dark:text-[#A0A0A0]">
                      Your cart is empty
                    </p>
                    <Link
                      to="/rentals"
                      className="inline-block mt-4 text-[#62397f] hover:underline"
                    >
                      Browse Rentals
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 mb-6 max-h-[300px] overflow-y-auto">
                      {cartItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex gap-3 p-3 bg-[#F5F5F5] dark:bg-[#2A2A2A] rounded-lg"
                        >
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-black dark:text-white truncate">
                              {item.name}
                            </p>
                            <p className="text-xs text-[#666666] dark:text-[#A0A0A0]">
                              Qty: {item.quantity}
                            </p>
                            <p className="text-sm font-bold text-[#62397f] dark:text-[#A78BFA]">
                              ₦{formatPrice(item.price * item.quantity)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-[#E9E9E9] dark:border-[#333333] pt-4 space-y-2 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#666666] dark:text-[#A0A0A0]">
                          Subtotal
                        </span>
                        <span className="font-semibold text-black dark:text-white">
                          ₦{formatPrice(calculateSubtotal())}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-[#666666] dark:text-[#A0A0A0]">
                            Service Fee
                          </span>
                          <span className="text-[10px] bg-[#F5F3FF] text-[#62397f] px-1.5 py-0.5 rounded-full font-bold">
                            {Number(settings?.service_charge_percent) || 0}%
                          </span>
                        </div>
                        <span className="font-semibold text-black dark:text-white">
                          ₦{formatPrice(calculateServiceCharge())}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-[#666666] dark:text-[#A0A0A0]">
                            VAT
                          </span>
                          <span className="text-[10px] bg-[#F5F3FF] text-[#62397f] px-1.5 py-0.5 rounded-full font-bold">
                            {Number(settings?.vat_percent) || 0}%
                          </span>
                        </div>
                        <span className="font-semibold text-black dark:text-white">
                          ₦{formatPrice(calculateVAT())}
                        </span>
                      </div>
                      <div className="border-t border-[#E9E9E9] dark:border-[#333333] pt-2 mt-2">
                        <div className="flex justify-between">
                          <span className="font-bold text-black dark:text-white">
                            Total
                          </span>
                          <span className="text-xl font-bold text-[#62397f] dark:text-[#A78BFA]">
                            ₦{formatPrice(calculateTotal())}
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={serviceLoading || cartItems.length === 0}
                      className="w-full px-8 py-4 lg:py-3 bg-gradient-to-r from-[#62397f] to-[#7C3AED] hover:from-[#7C3AED] hover:to-[#6D28D9] disabled:from-[#A78BFA] disabled:to-[#A78BFA] text-white font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_8px_24px_rgba(139,92,246,0.25)] disabled:cursor-not-allowed flex items-center justify-center gap-2 lg:text-base"
                    >
                      {serviceLoading ? (
                        <>
                          <Loader2 size={20} className="animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "Complete Order"
                      )}
                    </button>

                    <div className="mt-4 text-center px-4">
                      <p className="text-[11px] text-[#666666] dark:text-[#A0A0A0] leading-relaxed">
                        By completing this order, you agree to our{" "}
                        <button
                          type="button"
                          className="text-[#62397f] hover:underline font-medium"
                        >
                          terms and conditions.
                        </button>
                        <br />
                        An invoice will be generated and sent to your email, and
                        a member of our team will reach out to you shortly to confirm
                        your rental order and communicate logistics.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      <Footer />
      <FloatingActions />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </div>
  );
}
